import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'react-toastify';

const STORAGE_KEY = 'tnea_choicelist';

export interface ChoiceItem {
  id: number;                    // college id
  college_code: string;
  college_name: string;
  college_type: string;
  branch_code: string;
  branch_name: string;
  naac_grade?: string;
  district?: { id: number; name: string };
  annual_tuition_fee?: number;
  boys_accommodation_available?: boolean;
  girls_accommodation_available?: boolean;
}

interface ChoiceListContextValue {
  choiceList: ChoiceItem[];
  addToChoiceList: (item: ChoiceItem) => void;
  removeFromChoiceList: (collegeCode: string, branchCode: string) => void;
  isInChoiceList: (collegeCode: string, branchCode: string) => boolean;
  clearChoiceList: () => void;
}

const ChoiceListContext = createContext<ChoiceListContextValue | null>(null);

export const ChoiceListProvider = ({ children }: { children: ReactNode }) => {
  const [choiceList, setChoiceList] = useState<ChoiceItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choiceList));
  }, [choiceList]);

  const addToChoiceList = (item: ChoiceItem) => {
    if (choiceList.some(c => c.college_code === item.college_code && c.branch_code === item.branch_code)) {
      toast.info('This college + branch is already in your ChoiceList');
      return;
    }
    setChoiceList(prev => [...prev, item]);
    toast.success(`Added ${item.branch_code} — ${item.college_name.substring(0, 35)}…`);
  };

  const removeFromChoiceList = (collegeCode: string, branchCode: string) => {
    setChoiceList(prev => prev.filter(c => !(c.college_code === collegeCode && c.branch_code === branchCode)));
  };

  const isInChoiceList = (collegeCode: string, branchCode: string) =>
    choiceList.some(c => c.college_code === collegeCode && c.branch_code === branchCode);

  const clearChoiceList = () => {
    setChoiceList([]);
  };

  return (
    <ChoiceListContext.Provider value={{ choiceList, addToChoiceList, removeFromChoiceList, isInChoiceList, clearChoiceList }}>
      {children}
    </ChoiceListContext.Provider>
  );
};

export const useChoiceList = () => {
  const ctx = useContext(ChoiceListContext);
  if (!ctx) throw new Error('useChoiceList must be used inside ChoiceListProvider');
  return ctx;
};
